import { defineStore } from "pinia";
import {
	BERN_MINT,
	FIRST_POOL_PUBKEY,
	FLUXB_TOKEN,
	IRoute,
	IToken,
	ITokenAccount,
	SOL_MINT,
	SOL_TOKEN,
} from "@/shared";
import Vue, { computed, ref, watch } from "vue";
import { web3 } from "@project-serum/anchor";
import { useSolanaStore } from "./solanaStore";
import { useCacheStore } from "@/pinia/cacheStore";
import { PublicKey } from "@solana/web3.js";
import { TOKEN_PROGRAM_ID } from "@solana/spl-token";
import { useWalletStore } from "./walletStore";
import router from "@/router";
import { useAlertStore } from "./alertStore";
import store from "@/store";
import { parseCustomError } from "@/api/token_swap/errors";
import { createFluxBotSwap } from "@/api/endpoints";
import SignatureLoader from "@/mixins/SignatureLoader";

export const useSwapStore = defineStore("swap", () => {
	// store
	const solanaStore = useSolanaStore();
	const cacheStore = useCacheStore();
	const walletStore = useWalletStore();
	const alertStore = useAlertStore();

	// data
	const tokenA = ref<IToken>(SOL_TOKEN);
	const tokenB = ref<IToken>(FLUXB_TOKEN);
	const tokenABalance = ref<number>(0);
	const tokenBBalance = ref<number>(0);
	const route = ref<IRoute>({
		pool: null,
	});
	const jupiterRoute = ref<any>(null);
	const transactions = ref<any[]>([]);
	const signatures = ref<{ [i: string]: any }>({});
	const signatureOnChain = ref<{ [i: string]: any }>({});
	const signatureChannel = new BroadcastChannel("signature:status");

	// methods
	function mounted() {
		signatureChannel.onmessage = ({ data }) => {
			Vue.set(signatures.value, data.sig, data.status);
		};
	}
	function setTokenA(token: IToken): void {
		tokenA.value = token;
	}

	function setTokenB(token: IToken): void {
		tokenB.value = token;
	}

	function swapAToB(): void {
		const oldTokenA = tokenA.value;
		tokenA.value = tokenB.value;
		tokenB.value = oldTokenA;

		const oldBalanceA = tokenABalance.value;
		tokenABalance.value = tokenBBalance.value;
		tokenBBalance.value = oldBalanceA;
	}

	async function getTokenBalances(owner: web3.PublicKey): Promise<boolean> {
		if (!owner || !tokenA.value || !tokenB.value) return false;

		const tokenPDAA = solanaStore.tokenClient.getAssociatedTokenPDA(
			new PublicKey(tokenA.value.address.toString()),
			owner,
			new PublicKey(tokenA.value?.programID || TOKEN_PROGRAM_ID)
		);

		const tokenPDAB = solanaStore.tokenClient.getAssociatedTokenPDA(
			new PublicKey(tokenB.value.address.toString()),
			owner,
			new PublicKey(tokenB.value?.programID || TOKEN_PROGRAM_ID)
		);

		const multipleParsedAccounts =
			await solanaStore.client.getMultipleParsedAccounts(
				[owner, tokenPDAA, tokenPDAB],
				{ commitment: "processed" }
			);

		if (!multipleParsedAccounts.value) return false;

		const [walletBalance, accountA, accountB] = multipleParsedAccounts.value;

		tokenABalance.value = accountA
			? // @ts-ignore
			  Number(accountA.data?.parsed?.info.tokenAmount?.amount)
			: 0;
		tokenBBalance.value = accountB
			? // @ts-ignore
			  Number(accountB.data?.parsed?.info.tokenAmount?.amount)
			: 0;

		if (walletBalance) {
			if (tokenA.value.address.toString() === SOL_MINT) {
				tokenABalance.value += Number(walletBalance.lamports);
			}
			if (tokenB.value.address.toString() === SOL_MINT) {
				tokenBBalance.value += Number(walletBalance.lamports);
			}
		}

		cacheStore.updateTokenBalance(
			tokenA.value.address.toString(),
			tokenABalance.value
		);
		if (accountB !== null) {
			cacheStore.updateTokenBalance(
				tokenB.value.address.toString(),
				tokenBBalance.value
			);
		}
	}

	async function getPoolsDetail(wallet: web3.PublicKey): Promise<boolean> {
		const pools = await solanaStore.swapClient.getSwapPools(
			tokenA.value.address,
			tokenB.value.address
		);

		if (!pools.length) return false;

		const allowedMints = {
			[SOL_MINT]: true,
			[BERN_MINT]: true,
		};

		let lastLiquidity = 0;

		for (const pool of pools) {
			const isMintAAllowed = allowedMints[pool.account.mintA.toString()];
			const isMintBAllowed = allowedMints[pool.account.mintB.toString()];
			const isFirstPoolPubkey = pool.pubkey.equals(FIRST_POOL_PUBKEY);

			if (isMintAAllowed && isMintBAllowed && isFirstPoolPubkey) continue;

			const poolDetail = await solanaStore.swapClient.getPoolDetail(
				pool.pubkey,
				pool.account,
				wallet
			);

			const liquidity =
				poolDetail.tokenAccountA?.tokenAmount?.uiAmount +
				poolDetail.tokenAccountA?.tokenAmount?.uiAmount;

			if (liquidity > lastLiquidity) {
				lastLiquidity = liquidity;
				route.value.detail = poolDetail;
				route.value.pool = pool;
			}
		}

		if (route.value.pool) return true;

		const firstPool = pools[0];
		route.value.detail = await solanaStore.swapClient.getPoolDetail(
			firstPool.pubkey,
			firstPool.account,
			wallet
		);
		route.value.pool = firstPool;

		return true;
	}

	async function getJupiterRoute({
		amount,
		slippage,
	}: {
		amount: number;
		slippage: number;
	}): Promise<boolean> {
		const data = await solanaStore.jupiterClient.quote(
			tokenA.value.address,
			tokenB.value.address,
			amount,
			slippage * 100
		);

		if (data?.error) return false;

		jupiterRoute.value = data;

		return true;
	}

	function reset(): void {
		tokenABalance.value = 0;
		tokenBBalance.value = 0;
		route.value = {
			pool: null,
		};
		jupiterRoute.value = null;
	}

	async function doSwap({
		id = Date.now(),
		wallet,
		inMint,
		outMint,
		inAmount,
	}: any) {
		if (!walletStore.isConnected) {
			router.push("/auth/login");
			return;
		}

		try {
			alertStore.success("Creating Swap");

			// Track swap initiated event
			// @ts-ignore
			window.gtag("event", "swap_initiated", {
				id,
				inMint,
				outMint,
				inAmount
			});

			const sig = await _doSwap({ id, wallet, inMint, outMint, inAmount });

			// Track swap success event
			// @ts-ignore
			window.gtag("event", "swap_success", {
				id,
				sig,
				inMint,
				outMint,
				inAmount
			});

			setTimeout(() => {
				walletStore.refreshUserBalance();
			}, 1000);

			return sig;
		} catch (e: any) {
			store.commit("signatures/clear_status", id);
			console.error("Swap failed", e);
			alertStore.error(
				"Swap failed",
				parseCustomError(e.message || e).toString()
			);
			// @ts-ignore
			window.gtag("event", "swap_fail", {
				error: e,
				id,
				inMint,
				outMint,
				inAmount
			});
			throw new Error(e);
		}
	}

	async function _doSwap({
		id,
		wallet,
		inMint,
		outMint,
		inAmount,
	}): Promise<any> {
		if (walletStore.isBotWallet) {
			store.commit("signatures/add_signature", {
				sig: id,
				status: {},
			});
		}

		const resp = await createFluxBotSwap({
			wallet: wallet,
			inMint: inMint,
			outMint: outMint,
			inAmount: inAmount,
		});

		//Pass to txn polling
		if (resp.signature) {
			store.commit("signatures/update_status", {
				pendingSig: id,
				sig: resp.signature,
				status: {},
			});
			return resp.signature;
		}

		const transaction = web3.VersionedTransaction.deserialize(
			// @ts-ignore
			Buffer.from(resp.transaction, "base64")
		);

		console.log("Transaction", transaction);

		return SignatureLoader.methods.signAndSendTransaction(
			transaction,
			"swap_trade",
			[],
			() => {
				alertStore.success("Swap completed");
			},
			store,
			null,
			alertStore
		);
	}

	function getTransactionsHistory(wallet: string) {
		const key = `transactions:${wallet}`;
		const json = localStorage.getItem(key) ?? "[]";

		const txns = JSON.parse(json);
		transactions.value = txns;
		return txns;
	}

	async function setTransactionHistoryAttempt({ data, sig }: any) {
		await Promise.all([
			cacheStore.fetchMintPriceUSD(data.inMint),
			cacheStore.fetchMintPriceUSD(data.outMint),
		]);

		const inMintPrice = cacheStore.getTokenPricesUSD(data.inMint)?.priceUSD;
		const outMintPrice = cacheStore.getTokenPricesUSD(data.outMint)?.priceUSD;

		if (!inMintPrice || !outMintPrice) {
			throw new Error("Error to get mint price");
		}

		data.outAmount = (data.inAmount * inMintPrice) / outMintPrice;
		data.startBalance = cacheStore.getTokenBalance(data.outMint);
		data.sig = sig;

		signatureOnChain.value[sig] = data;
	}
	async function setTransactionHistory(data: any) {
		await walletStore.refreshUserBalance();

		data.createdAt = data.id
		data.id = data.sig;
		data.finalBalance = cacheStore.getTokenBalance(data.outMint);

		const txns = getTransactionsHistory(data.wallet);

		localStorage.setItem(
			`transactions:${data.wallet}`,
			JSON.stringify([...txns, data])
		);

		transactions.value = [...txns, data];
	}
	async function removeTransactionHistory({ idx, wallet }) {
		transactions.value.splice(idx, 1);
		localStorage.setItem(
			`transactions:${wallet}`,
			JSON.stringify([...transactions.value])
		);
	}
	async function resetTransactionHistory(wallet: string) {
		transactions.value = [];
		localStorage.setItem(`transactions:${wallet}`, JSON.stringify([]));
	}

	// getters
	const getTokenA = computed<ITokenAccount>(() => ({
		...tokenA.value,
		...cacheStore.userTokenAccounts[tokenA.value.address.toString()],
	}));
	const getTokenB = computed<ITokenAccount>(
		() =>
			cacheStore.userTokenAccounts[tokenA.value.address.toString()] ||
			tokenB.value
	);
	const getTokenABalance = computed<number>(
		() => cacheStore.userTokenAccounts[tokenA.value.address.toString()]?.amount
	);
	const getTokenBBalance = computed<number>(
		() => cacheStore.userTokenAccounts[tokenB.value.address.toString()]?.amount
	);
	const getRoute = computed<IRoute>(() => route.value);
	const getterJupiterRoute = computed<any>(() => jupiterRoute.value);

	// setup
	mounted();

	// watch
	watch(
		() => signatures.value,
		() => {
			for (const [sig, status] of Object.entries(signatures.value)) {
				if (status === null) return;

				const onChainData = signatureOnChain.value[sig];
				const successfully =
					(!status?.err && status?.confirmationStatus === "confirmed") ||
					status?.confirmationStatus === "finalized";

				if (onChainData && successfully) {
					setTransactionHistory(onChainData);
					Vue.delete(signatureOnChain.value, sig);
					Vue.delete(signatures.value, sig);
				}
			}
		},
		{ deep: true }
	);

	return {
		setTokenA,
		setTokenB,
		swapAToB,
		getTokenBalances,
		getPoolsDetail,
		getJupiterRoute,
		reset,
		doSwap,
		getTransactionsHistory,
		setTransactionHistoryAttempt,
		removeTransactionHistory,
		resetTransactionHistory,
		getterJupiterRoute,
		getTokenA,
		getTokenB,
		getTokenABalance,
		getTokenBBalance,
		getRoute,
		transactions,
	};
});
